// ── QuickBooks (Accounting -> QuickBooks tab) ──────────────────────────────
//
// Imitates GET /api/accounting/quickbooks/* and /api/accounting/mapping for the
// demo entity: an already-connected Intuit sandbox company, the six accounts,
// two Classes named like zones, and a dry-run-only post (posting is disabled,
// as it is on every real environment until a reviewed PR flips it). State is
// module-local so Save and Verify visibly stick for the session.
import type {
  AccountingMappingResponse, AccountingQuickBooksAccountRef, AccountingQuickBooksAccountsResponse,
  AccountingQuickBooksClassMapResponse, AccountingQuickBooksClassesResponse, AccountingQuickBooksCompanyResponse,
  AccountingQuickBooksConnectionStatusResponse, AccountingQuickBooksConnectResponse,
  AccountingQuickBooksPostDay, AccountingQuickBooksPostResponse,
  AccountingMappingUpsertRequest, AccountingQuickBooksClassMapUpsertRequest, AccountingQuickBooksPostRequest,
} from '@parkify/api-types'
import { ACCOUNTING_QUICKBOOKS_REQUIREMENTS } from '@parkify/api-types'
import { DEMO_CLIENT, DEMO_ZONES, zoneCode } from '../constants'

const REALM = '9341455000000000'
const COMPANY = 'Parkify Demo Sandbox Co'
const ENTITY_ID = `entity-${DEMO_CLIENT.id}`
const ENTITY = { clientId: DEMO_CLIENT.id, entityId: ENTITY_ID, entityName: `${DEMO_CLIENT.name} LLC`, active: true }

const account = (id: string, name: string, classification: AccountingQuickBooksAccountRef['classification'],
  accountType: AccountingQuickBooksAccountRef['accountType']): AccountingQuickBooksAccountRef =>
  ({ id, realmId: REALM, name, active: true, classification, accountType })

const ACCOUNTS: AccountingQuickBooksAccountRef[] = [
  account('35', 'Stripe Clearing', 'Asset', 'Other Current Asset'),
  account('36', 'Checking', 'Asset', 'Bank'),
  account('41', 'Parking Revenue', 'Revenue', 'Income'),
  account('42', 'Parking Refunds', 'Revenue', 'Income'),
  account('51', 'Chargeback Losses', 'Expense', 'Expense'),
  account('52', 'Parkify Fees', 'Expense', 'Expense'),
  account('53', 'Card Processing Fees', 'Expense', 'Other Expense'),
]

const CLASSES = DEMO_ZONES.slice(0, 2).map((z, i) => ({
  id: `c${i + 1}`, realmId: REALM, name: zoneCode(z.id), active: true as const,
}))

let mapping: AccountingMappingResponse['mapping'] = null
const classAssignments: Record<string, { classId: string; className: string }> = {}

const now = () => new Date().toISOString()
const actor = { actorId: 'demo-user', email: 'demo@parkify.com' }

function mappingState(): AccountingMappingResponse {
  return {
    configured: mapping !== null,
    ready: mapping?.verification.status === 'verified',
    provider: 'quickbooks',
    mapping,
    issues: [],
    requirements: ACCOUNTING_QUICKBOOKS_REQUIREMENTS,
    entity: ENTITY,
  }
}

export function demoQbMapping(): AccountingMappingResponse { return mappingState() }

export function demoQbPutMapping(body: AccountingMappingUpsertRequest): AccountingMappingResponse {
  mapping = {
    provider: 'quickbooks', clientId: DEMO_CLIENT.id, entityId: ENTITY_ID, realmId: REALM, companyName: COMPANY,
    accounts: body?.accounts ?? {},
    verification: { status: 'pending' },
    version: (mapping?.version ?? 0) + 1, createdAt: mapping?.createdAt ?? now(), updatedAt: now(), updatedBy: actor,
  }
  return mappingState()
}

export function demoQbVerify(): AccountingMappingResponse {
  if (!mapping) return { ...mappingState(), issues: [{ code: 'mapping_missing', message: 'Accounting mapping is required' }] }
  mapping = { ...mapping, verification: { status: 'verified', verifiedAt: now() }, version: mapping.version + 1, updatedAt: now() }
  return mappingState()
}

export function demoQbConnection(): AccountingQuickBooksConnectionStatusResponse {
  return {
    connected: true, provider: 'quickbooks', entity: ENTITY,
    connection: {
      realmId: REALM, companyName: COMPANY, scopes: ['com.intuit.quickbooks.accounting'],
      connectedAt: new Date(Date.now() - 2 * 86_400_000).toISOString(), connectedBy: actor,
      updatedAt: now(), lastVerifiedAt: mapping?.verification.verifiedAt ?? '',
    },
  }
}

export function demoQbConnect(): AccountingQuickBooksConnectResponse {
  // A shape-valid Intuit URL: the console validates it like a real one, then a
  // demo build declines to open it.
  return {
    provider: 'quickbooks',
    authorizationUrl: 'https://appcenter.intuit.com/connect/oauth2?demo=1',
    stateExpiresAt: new Date(Date.now() + 600_000).toISOString(),
  }
}

export function demoQbCompany(): AccountingQuickBooksCompanyResponse {
  return { provider: 'quickbooks', realmId: REALM, companyName: COMPANY, country: 'US', fetchedAt: now() }
}

export function demoQbAccounts(): AccountingQuickBooksAccountsResponse {
  return { provider: 'quickbooks', realmId: REALM, companyName: COMPANY, accounts: ACCOUNTS, fetchedAt: now() }
}

export function demoQbClasses(): AccountingQuickBooksClassesResponse {
  return { provider: 'quickbooks', realmId: REALM, companyName: COMPANY, classes: CLASSES, locationTracking: false, locations: [], fetchedAt: now() }
}

export function demoQbClassMap(): AccountingQuickBooksClassMapResponse {
  return {
    provider: 'quickbooks', entity: ENTITY, realmId: REALM,
    zones: DEMO_ZONES.map(z => {
      const a = classAssignments[z.id]
      const match = CLASSES.find(c => c.name.replace(/\s+/g, '').toLowerCase() === zoneCode(z.id).replace(/\s+/g, '').toLowerCase())
      return {
        zoneId: z.id, zoneCode: zoneCode(z.id), zoneName: z.name, routedToEntity: true,
        assignment: a ? { classId: a.classId, className: a.className, realmId: REALM, verifiedAt: now(), updatedAt: now(), updatedBy: actor } : null,
        ...(match ? { suggestion: { classId: match.id, className: match.name } } : {}),
      }
    }),
  }
}

export function demoQbPutClassMap(body: AccountingQuickBooksClassMapUpsertRequest): AccountingQuickBooksClassMapResponse {
  for (const a of body?.assignments ?? []) {
    const c = CLASSES.find(x => x.id === a.classId)
    if (a.classId && c) classAssignments[a.zoneId] = { classId: c.id, className: c.name }
    else delete classAssignments[a.zoneId]
  }
  return demoQbClassMap()
}

/** Dry run only: posting is disabled, so a real post is never offered. */
export function demoQbPost(body: AccountingQuickBooksPostRequest): AccountingQuickBooksPostResponse {
  const from: string = body?.from ?? ''
  const to: string = body?.to ?? ''
  const days: AccountingQuickBooksPostDay[] = []
  const verified = mapping?.verification.status === 'verified'
  let span = 0
  for (let t = Date.parse(`${from}T00:00:00Z`); t <= Date.parse(`${to}T00:00:00Z`) && span < 31; t += 86_400_000, span++) {
    const txnDate = new Date(t).toISOString().slice(0, 10)
    if ([0, 6].includes(new Date(t).getUTCDay())) {
      days.push({ txnDate, docNumber: `PKFY-${txnDate.replace(/-/g, '')}`, outcome: 'empty', entryCount: 0, debitCents: 0, creditCents: 0, blockers: [] })
      continue
    }
    // One entry per zone per day, like the live writer.
    for (const z of DEMO_ZONES) {
      const code = zoneCode(z.id)
      const mapped = !!classAssignments[z.id]
      days.push({
        txnDate, zoneId: z.id, zoneCode: code,
        docNumber: `PKFY-${txnDate.slice(2).replace(/-/g, '')}-${code.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}`,
        entryCount: 7, outcome: mapped && verified ? 'ready' : 'blocked',
        debitCents: 92_125, creditCents: 92_125,
        blockers: mapped && verified ? [] : [{
          code: mapped ? 'mapping_not_ready' : 'class_missing',
          message: mapped ? 'The account mapping is not verified.' : 'This zone needs a QuickBooks Class.',
          ...(mapped ? {} : { zoneIds: [z.id] }),
        }],
      })
    }
  }
  const totals = { empty: 0, ready: 0, posted: 0, already_posted: 0, blocked: 0, drift: 0, in_progress: 0, failed: 0 }
  for (const d of days) totals[d.outcome]++
  return {
    provider: 'quickbooks', dryRun: true, postingEnabled: false, entity: ENTITY, realmId: REALM,
    range: { from, to }, basis: 'cash', days, totals, generatedAt: now(),
  }
}
